/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext, useEffect, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import { Typography } from '@material-ui/core';
import InputAdornment from '@material-ui/core/InputAdornment';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Trans, useTranslation } from 'react-i18next';
import Tooltip from '../../common/Tooltip';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import { SubmissionContext } from '../context/SubmissionContextProvider';
import AssistiveText from './AssistiveText';

export const useTextWidgetStyles = makeStyles(theme => ({
  root: () => ({
    '& label': {
      transform: 'translate(0.875rem, 1rem) scale(1)',
      color: theme.palette.input,
      textOverflow: 'ellipsis',
      maxWidth: '90%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      minHeight: '1.2rem',
    },
  }),
  inputAdornment: {
    position: 'absolute',
  },
  '@global': {
    '.MuiInputAdornment-positionEnd': {
      paddingRight: '0rem',
    },
  },
  flexDiv: {
    display: 'flex',
    flexDirection: 'column',
  },
  customWarning: {
    '& .MuiOutlinedInput-marginDense fieldset': {
      border: `2px solid ${theme.palette.warning.main}`,
    },
    '& .MuiInputLabel-formControl': {
      color: theme.palette.mainText,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      maxWidth: '85%',
    },
  },
  customError: {
    '& .MuiOutlinedInput-marginDense fieldset': {
      border: `2px solid ${theme.palette.error.main}`,
    },
    '& .MuiInputLabel-formControl': {
      color: theme.palette.mainText,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      maxWidth: '85%',
    },
  },
  readOnly: {
    '& .MuiOutlinedInput-marginDense fieldset': {
      border: `1px solid ${theme.palette.lightBorder}`,
      backgroundColor: theme.palette.backgroundDisabled,
    },
    '& .MuiInputLabel-formControl': {
      color: theme.palette.lightBorder,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
  },
  title: {
    marginBottom: '3rem',
  },
  leadingValueTextField: ({ leadingValue }) => ({
    '&>div': {
      '&::before': {
        content: `"${leadingValue}"`,
        fontSize: theme.spacing(2),
        paddingLeft: theme.spacing(0.5),
      },
    },
  }),
}));

const TextWidget = ({
  id,
  required,
  readonly,
  disabled,
  type,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  autofocus,
  options,
  schema = {},
  uiSchema = {},
  rawErrors = [],
  formContext,
  onKeyDown,
  onPaste,
  registry = {},
  error,
  hasWarning,
  hasError,
  size = 'small',
  outerInputRenderer,
  innerInputRenderer,
  ...textFieldProps
}) => {
  const { schemaUtils } = registry;
  const { fieldsToClear, removeFieldToClear } = useContext(SubmissionContext);
  const leadingValue = schema?.leadingValue ?? uiSchema?.[uiSchemaProperties.UI_OPTIONS]?.leadingValue ?? undefined;
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  // Workaround for handling the custom error messages
  const _onChange = ({ target: { value: v } }) => {
    const { maxLength, config } = schema || {};
    const { trimToLength } = config || {};

    setErrorMessage(null);

    let fieldValue = v;

    if (trimToLength) {
      fieldValue = v.substring(0, trimToLength);
    }

    // If the use tries to input more than the allowed number of characters
    // Display an error message
    // And block the user from inputting more characters
    if (maxLength && fieldValue.length > maxLength) {
      setErrorMessage(t('validation.default.maxlength', { error: { limit: maxLength } }));
      fieldValue = v.substring(0, maxLength);
    }

    onChange(v.trim() === '' ? options.emptyValue : fieldValue.replace(/^\s+/, ''));
  };

  const classes = useTextWidgetStyles({ leadingValue });

  const _onBlur = ({ target: { value: v } }) => {
    setErrorMessage(null);
    onBlur(id, v);
  };

  const _onFocus = ({ target: { value: v } }) => onFocus(id, v);

  // custom tooltip text from config property of the jsonschema widget
  // or same for all oneOf dependencies with the ui:tooltip on the uiSchema
  const tooltip = schema?.config?.tooltip ?? uiSchema?.[uiSchemaProperties.UI_TOOLTIP];

  // Sanitize unsupported type definition
  let inputType = type || schema?.type;
  if (Array.isArray(inputType)) {
    [inputType] = inputType;
  }

  // When the read-only config is used, set the config value
  useEffect(() => {
    if (schema?.config?.type === 'fixed-value') {
      onChange(schema?.config?.value);
    }
  }, [schema?.config?.type, schema?.config?.value]);

  // clears the content of the field if it was marked by other form field
  useEffect(() => {
    if (fieldsToClear.includes(id)) {
      setTimeout(() => onChange(undefined), 5);
      removeFieldToClear(id);
    }
  }, [JSON.stringify(fieldsToClear)]);

  const displayLabel =
    schemaUtils && schemaUtils.getDisplayLabel && schemaUtils.getDisplayLabel(schema, uiSchema)
      ? (uiSchema?.config?.title && t(uiSchema?.config?.title)) || label || schema.title
      : false;

  const errorClass = !!error || !!errorMessage;
  const classNames = `${leadingValue && !!value ? classes.leadingValueTextField : ''} 
                      ${hasWarning ? classes.customWarning : ''} ${readonly ? classes.readOnly : ''}
                      ${hasError || errorMessage || errorClass ? classes.customError : ''}`;

  const inputAdornmentElement = (
    <div>
      <InputAdornment position="end" className={classes.inputAdornment}>
        <div className={classes.inputAdornment}>{innerInputRenderer && innerInputRenderer()}</div>

        {outerInputRenderer && outerInputRenderer()}
        {tooltip && <Tooltip title={<Trans i18nKey={tooltip} />} />}
      </InputAdornment>
    </div>
  );

  const title = uiSchema?.[uiSchemaProperties.UI_TITLE];
  if (uiSchema?.[uiSchemaProperties.UI_HIDDEN]) return null;

  const labelElem = document.getElementById(`${id}-label`);
  const onMouseEnterCallback = () => {
    const isOverflowing = labelElem?.offsetWidth < labelElem?.scrollWidth;
    if (isOverflowing) {
      setShowTooltip(true);
    } else {
      setShowTooltip(false);
    }
  };

  const onMouseExitCallback = () => {
    setShowTooltip(false);
  };

  return (
    <>
      {title && (
        <Typography className={classes.title} component="title" variant="h3" gutterBottom>
          {title}
        </Typography>
      )}
      <Tooltip placement="top" open={showTooltip} title={displayLabel}>
        <div
          onMouseEnter={onMouseEnterCallback}
          onMouseLeave={onMouseExitCallback}
          className={classes.flexDiv}
          tabIndex={-1}
        >
          <TextField
            id={id}
            data-testid={id}
            label={displayLabel}
            autoFocus={autofocus}
            required={required}
            disabled={disabled || readonly}
            size={size}
            type={inputType}
            value={value || value === 0 ? value : ''}
            error={rawErrors.length > 0}
            onChange={_onChange}
            onBlur={_onBlur}
            onFocus={_onFocus}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            className={`${classNames} ${classes.root}`}
            InputProps={{
              readOnly: schema?.config?.type === 'read-only',
              endAdornment: inputAdornmentElement,
            }}
            {...textFieldProps}
          />
          {errorMessage && <AssistiveText severity="warning">{errorMessage}</AssistiveText>}
        </div>
      </Tooltip>
    </>
  );
};

export default TextWidget;
